import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';

import {Flow, FlowFetchResponse} from '../_services/Flow';

import BarGraph from '../components/Dashboard/BarGraph';
import Metrics from '../components/Dashboard/Metrics';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {DataTable} from '../components/DataTable';
import {CustomizeDashboardModal} from '../components/CustomizeDashboardModal';

import {DashboardConfiguration, DashboardConfigurationStep} from '../types';
import {User} from '../_services/User'
import {cellRenderers} from '../_helpers/datatable-cellrenderers';
import {getReportMetricDetails} from '../_helpers/dashboard';
import {Cell} from '../components/Cell';

import IconComplete from '../_assets/images/complete.svg'
import IconIncomplete from '../_assets/images/incomplete.svg'
import IconDuration from '../_assets/images/duration.svg'
import LineGraph from '../components/Dashboard/LineGraph';

type DashboardPageProps = {};

type CustomizeModalState = DashboardConfiguration & {
    show: boolean;
};

type MetricDetails = {
    title: string;
    metric: '' | 'open' | 'completed' | 'average_duration' | 'started' | 'average-step-duration' | 'open-by-step',
    data: any;
};

const initialDashboardConfiguration: DashboardConfiguration = {
    durationDisplay: 'all_days',
    onlyBusinessHours: false,
    steps: [],
    fieldFilters: [],
    startDate: null,
    startDateText: '',
    endDate: null,
    endDateText: ''
};

const initialCustomizeModalState: CustomizeModalState = {
    show: false,
    ...initialDashboardConfiguration
};

const initialDetailData: MetricDetails = {
    title: '',
    metric: '',
    data: []
};

export const Dashboard: React.FunctionComponent<DashboardPageProps> = ({}) => {
    const {workflowId} = useParams();
    const history = useHistory();

    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [reportData, setReportData] = useState<any>({
        totalWorkflowsStarted: 0,
        totalWorkflowsOpen: 0,
        totalWorkflowsCompleted: 0,
        averageWorkflowDuration: {days: 0},
        averageWorkflowStepDuration: [],
        openWorkflowsByStep: [],
        averageStepDurationByMonth: []
    });

    const [detailData, setDetailData] = useState<MetricDetails>({...initialDetailData});
    const [detailDataIsLoading, setDetailDataIsLoading] = useState<boolean>(false);
    const [customizeModalState, setCustomizeModalState] = useState<CustomizeModalState>({...initialCustomizeModalState});

    const userDashboardConfiguration = User.getInstance().getDashboardConfiguration(workflowId);
    const [dashboardConfiguration, setDashboardConfiguration] = useState<DashboardConfiguration>({
        ...initialDashboardConfiguration,
        ...userDashboardConfiguration,
        startDate: userDashboardConfiguration.startDate ? new Date(userDashboardConfiguration.startDate) : null,
        endDate: userDashboardConfiguration.startDate ? new Date(userDashboardConfiguration.startDate) : null,
    });
    const [averageDurationByStepDisplay, setAverageDurationByStepDisplay] = useState<'days' | 'hours'>('days');

    const [showStepSumamry, setShowStepSummary] = useState<boolean>(false);

    useEffect(() => {
        setIsWorking(true);
        let requestCount = 4;

        Flow.getInstance().reportSteps(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    // if the length of steps returned from server doesn't
                    // match what is in the config, assume there has been a change
                    // and override the current config steps

                    if (response.json.length !== dashboardConfiguration.steps.length) {
                        const config = {
                            ...dashboardConfiguration,
                            steps: response.json.map((step: DashboardConfigurationStep) => ({
                                ...step,
                                checked: true // by default, all steps are available
                            }))
                        };
                        setDashboardConfiguration(config);
                        User.getInstance().setDashboardConfiguration(workflowId, config);
                    }
                } else {
                    throw new Error(response?.json?.message || 'Error getting dashboard configuration');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error getting dashboard configuration');
            })
            .finally(() => {
                if (--requestCount <= 0) {
                    setIsWorking(false);
                }
            });

        Flow.getInstance().reportMetrics(workflowId, dashboardConfiguration)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    console.log(response);
                    setReportData((state: any) => ({
                        ...state,
                        totalWorkflowsStarted: response.json.totalWorkflowsStarted,
                        totalWorkflowsOpen: response.json.totalWorkflowsOpen,
                        totalWorkflowsCompleted: response.json.totalWorkflowsCompleted,
                        averageWorkflowDuration: response.json.averageWorkflowDuration,
                    }));
                } else {
                    setErrorMessage(response?.json?.message || 'Error fetching data');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching data');
            })
            .finally(() => {
                if (--requestCount <= 0) {
                    setIsWorking(false);
                }
            });

        Flow.getInstance().reportAveStepDurationByMonth(workflowId, dashboardConfiguration)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setReportData((state: any) => ({
                        ...state,
                        averageStepDurationByMonth: Array.isArray(response?.json) ? response.json : []
                    }));
                }
            })
            .catch((error: any) => {
                console.log(error);
            })
            .finally(() => {})

        Flow.getInstance().reportAveDurationByStep(workflowId, dashboardConfiguration)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setReportData((state: any) => ({
                        ...state,
                        averageWorkflowStepDuration: Array.isArray(response?.json) ? response.json : []
                    }));
                    console.log('averageWorkflowStepDuration response', response);
                } else {
                    setErrorMessage(response.json.message || 'Error fetching data');
                }

                if (--requestCount <= 0) {
                    setIsWorking(false);
                }
            })
            .catch((error: any) => {
                if (--requestCount <= 0) {
                    setIsWorking(false);
                }
                setErrorMessage(error.message);
            });
        Flow.getInstance().reportOpenByStep(workflowId, dashboardConfiguration)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    if (response?.json && Object.keys(response.json).length > 0) {
                        setReportData((state: any) => ({
                            ...state,
                            openWorkflowsByStep: response.json || [],
                        }));
                    }
                } else {
                    setErrorMessage(response.json.message || 'Error fetching data');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error.message);
            })
            .finally(() => {
                if (--requestCount <= 0) {
                    setIsWorking(false);
                }
            })
    }, [workflowId, dashboardConfiguration]);

    const getAverageDurationDetailForStep = (stepId: string) => {
        if (!workflowId) {
            setErrorMessage('Error getting detail for selected Step');
        }

        setDetailDataIsLoading(true);
        Flow.getInstance().reportAveDurationByStep(workflowId, dashboardConfiguration, stepId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    // @ts-ignore
                    // tslint:disable-next-line:triple-equals
                    const step = dashboardConfiguration?.steps.find((step: DashboardConfigurationStep) => step.id == stepId);
                    const stepName = step && step?.name ? step.name : 'Untitled';

                    setDetailData({
                        title: 'Average Duration of Step: ' + stepName,
                        metric: 'average-step-duration',
                        data: response.json,
                    });
                } else {
                    setErrorMessage(response?.json?.message || 'Error fetching data');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching data');
            })
            .finally(() => {
                setDetailDataIsLoading(false);
            })
    };

    const getDetailForStep = (stepId: string) => {
        if (!workflowId) {
            setErrorMessage('Error getting detail for selected Step');
        }

        // NOTE: this is not ideal, and should be corrected at some point....
        // the graphs are built against the last 6mos of data, replicate that with the detail
        const date = new Date();
        date.setMonth(date.getMonth() - 6);
        date.setDate(1);

        let month = '' + (date.getMonth() + 1);
        let day = '' + date.getDate();
        const year = date.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        const reportMonth = [year, month, day].join('-');

        setDetailDataIsLoading(true);
        Flow.getInstance().search(false, {
            page: 1,
            page_size: 1000,
            workflow: workflowId,
            fieldFilters: [
                {
                    workflowField: 'date_created',
                    workflowSearchFieldValueText: reportMonth,
                    workflowFieldOperator: '>'
                },
                {
                    workflowField: 'workflow_final_status',
                    workflowSearchFieldValueText: 'pending',
                    workflowFieldOperator: 'is'
                },
                {
                    workflowField: 'workflow_step',
                    workflowSearchFieldValueText: stepId,
                    workflowFieldOperator: 'is'
                },
                ...(dashboardConfiguration.fieldFilters.map((filter) => ({
                    workflowField: filter?.workflowField?.key || '',
                    workflowSearchFieldValueText: filter?.workflowFieldSearchValueOption?.value || filter?.workflowSearchFieldValueText || '',
                    workflowFieldOperator: filter?.workflowFieldOperator?.value || ''
                })))
            ]
        })
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    // @ts-ignore
                    // tslint:disable-next-line:triple-equals
                    const step = dashboardConfiguration?.steps.find((step: DashboardConfigurationStep) => step.id == stepId);
                    const stepName = step && step?.name ? step.name : 'Untitled';

                    setDetailData({
                        title: 'Open by Step: ' + stepName,
                        metric: 'open-by-step',
                        data: response.json,
                    });
                } else {
                    throw Error(response?.json?.message || 'Error getting detail for selected Step');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error getting detail for selected Step');
            })
            .finally(() => {
                setDetailDataIsLoading(false);
            });
    };

    const handleExport = () => {
        const csv: any = [];

        console.log(detailData);

        // add the headers
        const headers = detailData.data.columns.map((column: any): any => {
            if (column.prop === 'duration') {
                return [
                    '"Duration Hours"',
                    '"Duration Days"',
                    '"Duration Business Hours"',
                    '"Duration Business Days"',
                ].join(',')
            } else {
                return '"' + column.label
                    .replace(/(\r\n|\n|\r)/gm, '')
                    .replace(/(\s\s)/gm, ' ')
                    .replace(/"/g, '""') + '"';
            }
        });

        csv.push(headers.join(','));

        const rows: any = detailData.data.entries.map((item: any, index: number) => {
            const row = detailData.data.columns.map((column: any): any => {
                if (column.prop === 'lineNumber') {
                    return `"${index + 1}"`;
                } else if (column.prop === 'duration') {
                    return [
                        item.hasOwnProperty('duration_hours') && typeof item['duration_hours']['replace'] === 'function' ?
                            '"' + item['duration_hours']
                                .replace(/(\r\n|\n|\r)/gm, '')
                                .replace(/(\s\s)/gm, ' ')
                                .replace(/"/g, '""') + '"'
                            :
                            '""',
                        item.hasOwnProperty('duration_days') && typeof item['duration_days']['replace'] === 'function' ?
                            '"' + item['duration_days']
                                .replace(/(\r\n|\n|\r)/gm, '')
                                .replace(/(\s\s)/gm, ' ')
                                .replace(/"/g, '""') + '"'
                            :
                            '""',
                        item.hasOwnProperty('duration_business_hours') && typeof item['duration_business_hours']['replace'] === 'function' ?
                            '"' + item['duration_business_hours']
                                .replace(/(\r\n|\n|\r)/gm, '')
                                .replace(/(\s\s)/gm, ' ')
                                .replace(/"/g, '""') + '"'
                            :
                            '""',
                        item.hasOwnProperty('duration_business_days') && typeof item['duration_business_days']['replace'] === 'function' ?
                            '"' + item['duration_business_days']
                                .replace(/(\r\n|\n|\r)/gm, '')
                                .replace(/(\s\s)/gm, ' ')
                                .replace(/"/g, '""') + '"'
                            :
                            '""',
                    ].join(',');
                } else {
                    return item.hasOwnProperty(column.prop) && typeof item[column.prop]['replace'] === 'function' ?
                        '"' + item[column.prop]
                            .replace(/(\r\n|\n|\r)/gm, '')
                            .replace(/(\s\s)/gm, ' ')
                            .replace(/"/g, '""') + '"'
                        :
                        '""';
                }
            });
            return row.join(',');
        });
        csv.push(...rows);

        const csvString = csv.join('\n');
        const filename = detailData.title.toLowerCase().replace(/[^0-9a-z-]+/, '_') + '.csv';
        const link = document.createElement('a');
        link.style.display = 'none';
        link.setAttribute('target', '_blank');
        link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvString));
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const {
        totalWorkflowsStarted,
        totalWorkflowsOpen,
        totalWorkflowsCompleted,
        averageWorkflowDuration,
        averageWorkflowStepDuration,
        openWorkflowsByStep,
        averageStepDurationByMonth
    } = reportData;

    const sortLike = (refArr: DashboardConfigurationStep[]) => (a: any, b: any) => {
        // tslint:disable-next-line:triple-equals
        const refAIdx = refArr.findIndex((step: DashboardConfigurationStep) => step.checked && step.id == a.feed_id);
        // tslint:disable-next-line:triple-equals
        const refBIdx = refArr.findIndex((step: DashboardConfigurationStep) => step.checked && step.id == b.feed_id);

        if (refAIdx < refBIdx) {
            return -1;
        } else if (refAIdx > refBIdx) {
            return 1;
        } else {
            return 0;
        }
    };

    // tslint:disable-next-line:triple-equals
    const filteredAverageWorkflowStepDuration = averageWorkflowStepDuration?.filter((item: any) => dashboardConfiguration.steps.findIndex((step: DashboardConfigurationStep) => step.checked && step.id == item.feed_id) >= 0);
    // tslint:disable-next-line:triple-equals
    const filteredOpenWorkflowsByStep = openWorkflowsByStep?.filter((item: any) => dashboardConfiguration.steps.findIndex((step: DashboardConfigurationStep) => step.checked && step.id == item.feed_id) >= 0);
    const sortedFilteredAverageWorkflowStepDuration = filteredAverageWorkflowStepDuration?.sort(sortLike(dashboardConfiguration.steps));
    const sortedFilteredOpenWorkflowsByStep = filteredOpenWorkflowsByStep?.sort(sortLike(dashboardConfiguration.steps));
    // tslint:disable-next-line:triple-equals
    const filteredAverageStepDurationByMonth = averageStepDurationByMonth?.filter((item: any) => dashboardConfiguration.steps.findIndex((step: DashboardConfigurationStep) => step.checked && step.id == item.feed_id) >= 0);

    const dateRangeText = [
        !dashboardConfiguration.startDate && !dashboardConfiguration.endDate && 'last 2 months',
        dashboardConfiguration.startDateText && ` from ${dashboardConfiguration.startDateText}`,
        dashboardConfiguration.endDateText && ` to ${dashboardConfiguration.endDateText}`
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div id="dashboard">
            {isWorking && <LoadingIndicator/>}
            {errorMessage && <p className="alert alert-danger">{errorMessage}</p>}
            <div className="container">
                <div className="d-flex justify-content-between">
                    <div/>
                    <button type="button"
                            className="btn btn-link"
                            onClick={() => {
                                setCustomizeModalState({
                                    ...initialCustomizeModalState,
                                    ...dashboardConfiguration,
                                    show: true,
                                });
                            }}
                    >Customize
                    </button>
                </div>
                {totalWorkflowsOpen !== null && totalWorkflowsCompleted !== null && averageWorkflowStepDuration !== null &&
                <Metrics metrics={[
                    {
                        title: '# Started',
                        subTitle: dateRangeText,
                        number: totalWorkflowsStarted,
                        icon: <IconIncomplete style={{width: '12px'}}/>,
                        variant: 'primary',
                        onClick: () => getReportMetricDetails('started', dashboardConfiguration, workflowId, setDetailDataIsLoading, setDetailData)
                    },
                    {
                        title: '# Open',
                        subTitle: '-',
                        number: totalWorkflowsOpen,
                        icon: <IconIncomplete style={{width: '12px'}}/>,
                        variant: 'warning',
                        onClick: () => getReportMetricDetails('open', dashboardConfiguration, workflowId, setDetailDataIsLoading, setDetailData)
                    },
                    {
                        title: '# Completed',
                        subTitle: dateRangeText,
                        number: totalWorkflowsCompleted,
                        icon: <IconComplete style={{width: '12px'}}/>,
                        variant: 'success',
                        onClick: () => getReportMetricDetails('completed', dashboardConfiguration, workflowId, setDetailDataIsLoading, setDetailData)
                    },
                    {
                        title: 'Ave Duration',
                        subTitle: dateRangeText,
                        number: averageWorkflowDuration.days,
                        unit: 'days',
                        icon: <IconDuration style={{width: '12px'}}/>,
                        variant: 'primary',
                        onClick: () => getReportMetricDetails('average_duration', dashboardConfiguration, workflowId, setDetailDataIsLoading, setDetailData)
                    }
                ]}/>}
                {!showStepSumamry &&
                    <div className="row">
                        <div className="col">
                            {sortedFilteredAverageWorkflowStepDuration?.length > 0 &&
                                <>
                                    <div className="d-flex justify-content-between" style={{marginTop: '15px', marginBottom: '-13px'}}>
                                        <div>
                                            <button type="button"
                                                    className="btn btn-link btn-sm"
                                                    onClick={() => setShowStepSummary(true)}
                                            >by Month
                                            </button>
                                        </div>
                                        <div className="btn-group" role="group" aria-label="Basic example">
                                            <button type="button"
                                                    className={[
                                                        'btn btn-sm',
                                                        averageDurationByStepDisplay === 'days' ? 'btn-danger' : 'btn-outline-danger'
                                                    ].filter(Boolean).join(' ')}
                                                    onClick={() => setAverageDurationByStepDisplay('days')}
                                            >Days
                                            </button>
                                            <button type="button"
                                                    className={[
                                                        'btn btn-sm',
                                                        averageDurationByStepDisplay === 'hours' ? 'btn-danger' : 'btn-outline-danger'
                                                    ].filter(Boolean).join(' ')}
                                                    onClick={() => setAverageDurationByStepDisplay('hours')}
                                            >Hours
                                            </button>
                                        </div>
                                    </div>
                                    <BarGraph title="Average Duration by Step"
                                              dataKey={((dashboardConfiguration.onlyBusinessHours || dashboardConfiguration.durationDisplay === 'business_days') ? 'business_' : '') + averageDurationByStepDisplay}
                                              data={sortedFilteredAverageWorkflowStepDuration}
                                              tooltipFormatter={(value: any) => [value + ' ' + averageDurationByStepDisplay]}
                                              onClick={(dataItem: any) => {
                                                  getAverageDurationDetailForStep(dataItem.feed_id);
                                              }}
                                    />
                                </>
                            }
                        </div>
                    </div>
                }

                {showStepSumamry &&
                    <div className="row">
                        <div className="col">
                            {filteredAverageStepDurationByMonth?.length > 0 &&
                                <>
                                    <div style={{marginTop: '15px', marginBottom: '-13px'}}>
                                        <button type="button"
                                                className="btn btn-link btn-sm"
                                                onClick={() => setShowStepSummary(false)}
                                        >by Step
                                        </button>
                                    </div>
                                    <LineGraph
                                        title="Average Step Duration by Month"
                                        data={filteredAverageStepDurationByMonth}
                                        xDataKey={'mnth'}
                                        yDataKey={'value'}
                                    />
                                </>
                            }
                        </div>
                    </div>
                }

                <div className="row">
                    <div className="col">
                        {sortedFilteredOpenWorkflowsByStep?.length > 0 &&
                        <BarGraph title="Open by Step"
                                  data={sortedFilteredOpenWorkflowsByStep}
                                  onClick={(dataItem: any) => {
                                      getDetailForStep(dataItem.feed_id);
                                  }}
                        />
                        }
                    </div>
                </div>

                <div className="row mt-5">
                    <div className="col-12">
                        {detailDataIsLoading && <LoadingIndicator/>}
                        {detailData && detailData?.data.hasOwnProperty('entries') &&
                        <>
                            <div className="d-flex justify-content-between">
                                {detailData.title && <h2>{detailData.title}</h2>}
                                <button type="button"
                                        className="btn btn-primary"
                                        onClick={() => handleExport()}
                                >Export
                                </button>
                            </div>
                            <DataTable
                                id="search-table"
                                striped={true}
                                classNames="table-borderless"
                                headings={detailData.data.columns}
                                rows={detailData.data.entries}
                                onRowClick={(row: any) => {
                                    history.push('/entry/' + row.id);
                                }}
                                cellRenderers={{
                                    ...cellRenderers,
                                    duration: (id: any, value: any) => (
                                        <Cell
                                            key={id}
                                            content={
                                                <div dangerouslySetInnerHTML={{__html: value}}/>
                                            }
                                            classNames={`cell-${id} cell-id`}
                                        />
                                    ),
                                }}
                            />
                        </>
                        }
                    </div>
                </div>

            </div>

            {customizeModalState.show &&
            <CustomizeDashboardModal
                workflowId={workflowId}
                options={{
                    ...customizeModalState
                }}
                saveConfiguration={(options) => {
                    setDashboardConfiguration({...options});
                    User.getInstance().setDashboardConfiguration(workflowId, options);
                    setCustomizeModalState({
                        ...initialCustomizeModalState
                    });
                }}
                close={() => {
                    setCustomizeModalState({
                        ...initialCustomizeModalState
                    });
                }}
            />
            }
        </div>
    );
};